/**
 * Pure audio encoding/decoding utilities for the call screen.
 *
 * - encode/decode: base64 <-> Uint8Array for WebSocket audio transport
 * - resampleFloat32ToPcm16: resamples device input to the negotiated wire rate
 * - decodeAudioData: resamples provider PCM to the AudioContext's native rate
 */

/** Encode a Uint8Array to a base64 string. */
export function encode(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** Decode a base64 string to a Uint8Array. */
export function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function floatSampleToPcm16(sample: number): number {
  return sample <= -1 ? -32_768 : sample >= 1 ? 32_767 : Math.round(sample * 32_767);
}

/** Stateful mono resampler that preserves interpolation phase between callbacks. */
export class StreamingPcm16Resampler {
  private sourcePosition = 0;
  private pendingInput = new Float32Array();

  constructor(
    readonly sourceSampleRate: number,
    readonly targetSampleRate: number,
  ) {
    if (!Number.isFinite(sourceSampleRate) || sourceSampleRate <= 0) {
      throw new Error('sourceSampleRate must be positive');
    }
    if (!Number.isFinite(targetSampleRate) || targetSampleRate <= 0) {
      throw new Error('targetSampleRate must be positive');
    }
  }

  process(input: Float32Array): Int16Array {
    const combined = new Float32Array(this.pendingInput.length + input.length);
    combined.set(this.pendingInput);
    combined.set(input, this.pendingInput.length);
    const ratio = this.sourceSampleRate / this.targetSampleRate;
    const output: number[] = [];

    while (this.sourcePosition < combined.length) {
      const lower = Math.floor(this.sourcePosition);
      const fraction = this.sourcePosition - lower;
      const upper = lower + 1;
      if (upper >= combined.length && fraction > Number.EPSILON) break;
      const upperSample = upper < combined.length ? combined[upper] : combined[lower];
      output.push(floatSampleToPcm16(
        combined[lower] + fraction * (upperSample - combined[lower]),
      ));
      this.sourcePosition += ratio;
    }

    const consumed = Math.min(Math.floor(this.sourcePosition), combined.length);
    this.pendingInput = combined.slice(consumed);
    this.sourcePosition -= consumed;
    return Int16Array.from(output);
  }
}

/** Resample mono device audio to provider-native PCM16 using linear interpolation. */
export function resampleFloat32ToPcm16(
  input: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number,
  streaming?: StreamingPcm16Resampler,
): Int16Array {
  if (streaming) return streaming.process(input);
  const targetLength = Math.floor(input.length * targetSampleRate / sourceSampleRate);
  const output = new Int16Array(targetLength);
  const ratio = sourceSampleRate / targetSampleRate;
  for (let index = 0; index < targetLength; index++) {
    const sourceIndex = index * ratio;
    const lower = Math.floor(sourceIndex);
    const upper = Math.min(lower + 1, input.length - 1);
    const fraction = sourceIndex - lower;
    const sample = input[lower] + fraction * (input[upper] - input[lower]);
    output[index] = floatSampleToPcm16(sample);
  }
  return output;
}

/** Buffer resampled PCM until full negotiated provider chunks are available. */
export function appendPcm16Frames(
  pending: Int16Array,
  incoming: Int16Array,
  samplesPerChunk: number,
): { chunks: Int16Array[]; remainder: Int16Array } {
  if (!Number.isInteger(samplesPerChunk) || samplesPerChunk <= 0) {
    throw new Error('samplesPerChunk must be a positive integer');
  }
  const combined = new Int16Array(pending.length + incoming.length);
  combined.set(pending, 0);
  combined.set(incoming, pending.length);
  const fullChunkCount = Math.floor(combined.length / samplesPerChunk);
  const chunks: Int16Array[] = [];
  for (let index = 0; index < fullChunkCount; index++) {
    const start = index * samplesPerChunk;
    chunks.push(combined.slice(start, start + samplesPerChunk));
  }
  return {
    chunks,
    remainder: combined.slice(fullChunkCount * samplesPerChunk),
  };
}

/**
 * Decode raw PCM Int16 audio data into an AudioBuffer, resampling from
 * `sourceSampleRate` to the AudioContext's native sample rate using
 * linear interpolation.
 */
export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sourceSampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const sourceFrameCount = dataInt16.length / numChannels;

  // Resample from the event's provider-native rate to AudioContext's native rate.
  const targetSampleRate = ctx.sampleRate;
  const resampleRatio = targetSampleRate / sourceSampleRate;
  const targetFrameCount = Math.floor(sourceFrameCount * resampleRatio);

  const buffer = ctx.createBuffer(numChannels, targetFrameCount, targetSampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < targetFrameCount; i++) {
      // Linear interpolation for upsampling
      const srcIndex = i / resampleRatio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, sourceFrameCount - 1);
      const t = srcIndex - srcIndexFloor;

      const sample1 = dataInt16[srcIndexFloor * numChannels + channel] / 32768.0;
      const sample2 = dataInt16[srcIndexCeil * numChannels + channel] / 32768.0;

      channelData[i] = sample1 + t * (sample2 - sample1);
    }
  }
  return buffer;
}
